import { useState } from "react";
import { User } from "../../../models/user";
import * as UserApi from "../../../network/user_api";
import { SplashScreen } from "../../../models/splashScreens";
import { ProfileImage } from "../../../models/profileImages";
import { CardTheme } from "../../../models/cardThemes";
import Cookies from "js-cookie";
import styles from "./CollectibleSelector.module.scss";
import Divider from "../../../components/Decors/Divider/Divider";
import CloseButton from "../../../components/Buttons/Close/CloseButton";
import { useTranslation } from "react-i18next";

interface CollectibleSelectorProps {
  user: User;
  activeCollectible: string;
  collectibles: SplashScreen[] | ProfileImage[] | CardTheme[];
  collectableType: "splashScreens" | "profileImages" | "cardThemes";
  onImageChange: (updatedUser: User) => void;
}

const CollectibleSelector = ({
  user,
  activeCollectible,
  collectibles,
  collectableType,
  onImageChange,
}: CollectibleSelectorProps) => {
  const { t, i18n } = useTranslation();

  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [activeImage, setActiveImage] = useState(activeCollectible);
  const handleSetImage = async (activeImageFileName: string) => {
    if (collectableType === "profileImages") {
      try {
        const updatedUser = await UserApi.updateUser({
          ...user,
          xp: 0,
          activeProfileImage: activeImageFileName,
        });
        setActiveImage(activeImageFileName);
        Cookies.set("activeProfileImage", activeImageFileName);
        onImageChange(updatedUser);
        setIsGalleryOpen(false);
      } catch (error) {
        console.error("Failed to update profile image:", error);
        alert("Failed to update profile image.");
      }
    } else if (collectableType === "splashScreens") {
      try {
        const updatedUser = await UserApi.updateUser({
          ...user,
          xp: 0,
          activeSplashScreen: activeImageFileName,
        });
        setActiveImage(activeImageFileName);
        Cookies.set("activeSplashScreen", activeImageFileName);
        onImageChange(updatedUser);
        setIsGalleryOpen(false);
      } catch (error) {
        console.error("Failed to update splash screen:", error);
        alert("Failed to update splash screen.");
      }
    } else if (collectableType === "cardThemes") {
      try {
        const updatedUser = await UserApi.updateUser({
          ...user,
          xp: 0,
          activeCardTheme: activeImageFileName,
        });
        setActiveImage(activeImageFileName);
        Cookies.set("activeCardTheme", activeImageFileName);
        onImageChange(updatedUser);
        setIsGalleryOpen(false);
      } catch (error) {
        console.error("Failed to update card theme:", error);
        alert("Failed to update card theme.");
      }
    }
  };

  return (
    <div className={styles.imageSelector}>
      <img
        src={`${process.env.REACT_APP_API_URL}/images/collectibles/${collectableType}/${activeCollectible}`}
        alt={user.activeSplashScreen}
        className={styles.activeImage}
        onClick={() => setIsGalleryOpen(!isGalleryOpen)}
      />

      {isGalleryOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <CloseButton onDismiss={() => setIsGalleryOpen(false)} />
            <h2 className={styles.modalTitle}>
              {t(`store.${collectableType}`)}
            </h2>
            <div className={styles.smallDivider}>
              <Divider />
            </div>
            <div className={styles.galleryGrid}>
              {collectibles.map((collectible, index) => (
                <div
                  className={styles.imageContainer}
                  key={index}
                  onClick={() => handleSetImage(collectible.fileName)}
                >
                  <img
                    src={`${process.env.REACT_APP_API_URL}/images/collectibles/${collectableType}/${collectible.fileName}`}
                    alt={
                      i18n.language === "hu"
                        ? collectible.hungarianTitle
                        : collectible.englishTitle
                    }
                    className={`${styles.galleryImage} ${
                      collectible.fileName === activeImage
                        ? styles.activeImageContainer
                        : ""
                    }`}
                  />
                  <span className={styles.setButton}>Set</span>
                  <h3 className={styles.collectibleTitle}>
                    {i18n.language === "hu"
                      ? collectible.hungarianTitle
                      : collectible.englishTitle}
                  </h3>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollectibleSelector;
